import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import {
  TrendingUp,
  People,
  Work,
  Receipt,
  InfoOutlined,
  Build,
} from '@mui/icons-material';
import { Tooltip as MuiTooltip } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useLeads } from '../contexts/LeadsContext';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface KPIData {
  totalCustomers: number;
  totalLeads: number;
  activeProjects: number;
  completedProjects: number;
  totalRevenue: number;
  pendingInvoices: number;
  newLeadsThisMonth: number;
  projectsCompletedThisMonth: number;
}

interface Activity {
  type: string;
  title: string;
  description: string;
  created_at: string;
}

interface PipelineData {
  name: string;
  value: number;
  total_value?: number;
}

interface MonthlyStats {
  month: string;
  new: number;
  junk: number;
  outOfArea: number;
  newSpray: number;
  noReply: number;
  notInterested: number;
  wrongNumber: number;
  quoted: number;
  notReady: number;
  sold: number;
  total: number;
}

interface LeadStatusStats {
  category: string;
  [key: string]: string | number; // Allow dynamic month properties
  total: number;
}

interface LeadSource {
  source: string;
  count: number;
  converted: number;
  conversionRate: string;
}

// More distinct colors for pie chart segments to avoid duplication
const KPI_COLORS = [
  '#3498db', // Blue - New
  '#2ecc71', // Green - Contacted
  '#f39c12', // Orange - Not Ready
  '#9b59b6', // Purple - Quoted
  '#1abc9c', // Turquoise - Quoted (Deposit)
  '#27ae60', // Dark Green - Won
  '#e74c3c', // Red - Lost
  '#95a5a6', // Gray - Other
];

// Helper function to get last 6 months
const getLast6Months = () => {
  const months = [];
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const currentDate = new Date();
  
  for (let i = 5; i >= 0; i--) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    months.push(monthNames[date.getMonth()]);
  }
  
  return months;
};

// Memoized LineChart component to prevent re-renders
const MemoizedLineChart = React.memo(({ data }: { data: any[] }) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart
      data={data}
      margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="month" />
      <YAxis />
      <Tooltip />
      <Legend />
      <Line type="monotone" dataKey="new" stroke="#9b59b6" name="New" strokeWidth={2} isAnimationActive={false} />
      <Line type="monotone" dataKey="noReply" stroke="#95a5a6" name="No Reply" strokeWidth={2} isAnimationActive={false} />
      <Line type="monotone" dataKey="notReady" stroke="#f39c12" name="Not Ready" strokeWidth={2} isAnimationActive={false} />
      <Line type="monotone" dataKey="contacted" stroke="#3498db" name="Contacted" strokeWidth={2} isAnimationActive={false} />
      <Line type="monotone" dataKey="quoted" stroke="#1abc9c" name="Quoted" strokeWidth={2} isAnimationActive={false} />
      <Line type="monotone" dataKey="sold" stroke="#27ae60" name="Won" strokeWidth={2} isAnimationActive={false} />
      <Line type="monotone" dataKey="lost" stroke="#e74c3c" name="Lost" strokeWidth={2} isAnimationActive={false} />
    </LineChart>
  </ResponsiveContainer>
));

export default function Dashboard() {
  const { leads, leadsLoading, refreshLeads } = useLeads();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activities, setActivities] = useState<Activity[]>([]);
  const [pipelineData, setPipelineData] = useState<PipelineData[]>([]);

  // Debug function to send logs to server
  const sendDebugLog = (message: string, data: any = {}) => {
    const logData = {
      message: `[Dashboard] ${message}`,
      data,
      timestamp: new Date().toISOString(),
      page: 'dashboard',
      isMobile,
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
    };
    
    fetch('/api/debug-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logData),
    }).catch(() => {});
  };
  // Remove state for monthlyStats as we'll use useMemo instead
  // const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [leadStatusStats, setLeadStatusStats] = useState<LeadStatusStats[]>(() => {
    const months = getLast6Months();
    const emptyMonthData = Object.fromEntries(months.map(m => [m, 0]));
    return [
      { category: 'NEW', ...emptyMonthData, total: 0 },
      { category: 'CONTACTED', ...emptyMonthData, total: 0 },
      { category: 'QUOTED', ...emptyMonthData, total: 0 },
      { category: 'WON', ...emptyMonthData, total: 0 }
    ];
  });
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [displayMonths, setDisplayMonths] = useState<string[]>(() => getLast6Months());
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [backendKPIData, setBackendKPIData] = useState<any>(null);
  const [followUpYear, setFollowUpYear] = useState<number>(new Date().getFullYear());
  const [leadSourcesPeriod, setLeadSourcesPeriod] = useState<number>(6); // Default 6 months

  // Memoize monthly stats calculation to prevent re-rendering on mouse movement
  const monthlyStats = useMemo(() => {
    try {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const months: string[] = [];
      const monthNumbers: number[] = [];
      const monthYears: number[] = [];
      
      for (let i = 5; i >= 0; i--) {
        let monthIndex = currentMonth - i;
        let year = currentYear;
        if (monthIndex < 0) {
          monthIndex += 12;
          year = currentYear - 1;
        }
        months.push(monthNames[monthIndex]);
        monthNumbers.push(monthIndex);
        monthYears.push(year);
      }
      
      // Always return a structure, even with no data
      const defaultStat = {
        new: 0,
        contacted: 0,
        junk: 0,
        outOfArea: 0,
        newSpray: 0,
        noReply: 0,
        notInterested: 0,
        wrongNumber: 0,
        alreadySorted: 0,
        quoted: 0,
        notReady: 0,
        sold: 0,
        total: 0
      };
      
      // If no leads, return empty structure
      if (!leads || !Array.isArray(leads) || leads.length === 0) {
        return months.map(month => ({
          month,
          ...defaultStat
        }));
      }
      
      const stats = months.map((month, index) => {
        const monthNum = monthNumbers[index];
        const year = monthYears[index];
        const monthLeads = leads.filter(lead => {
          if (!lead || !lead.created_at) return false;
          try {
            const leadDate = new Date(lead.created_at);
            return !isNaN(leadDate.getTime()) && 
                   leadDate.getMonth() === monthNum && 
                   leadDate.getFullYear() === year;
          } catch {
            return false;
          }
        });
        
        return {
          month,
          new: monthLeads.filter(l => l.status === 'new').length,
          contacted: monthLeads.filter(l => l.status === 'contacted').length,
          junk: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'junk').length,
          outOfArea: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'out_of_area').length,
          newSpray: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'new_spray').length,
          noReply: monthLeads.filter(l => l.status === 'no_reply').length,
          notInterested: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'not_interested').length,
          wrongNumber: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'wrong_number').length,
          alreadySorted: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'already_sorted').length,
          price: monthLeads.filter(l => l.status === 'lost' && l.lost_status === 'price').length,
          quoted: monthLeads.filter(l => l.status === 'quoted').length,
          notReady: monthLeads.filter(l => l.status === 'not_ready').length,
          sold: monthLeads.filter(l => l.status === 'won').length,
          lost: monthLeads.filter(l => l.status === 'lost').length,
          total: monthLeads.length
        };
      });
      
      return stats;
    } catch (error) {
      console.error('Error calculating monthly stats:', error);
      // Return default structure on error
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN'];
      return monthNames.map(month => ({
        month,
        new: 0,
        contacted: 0,
        junk: 0,
        outOfArea: 0,
        newSpray: 0,
        noReply: 0,
        notInterested: 0,
        wrongNumber: 0,
        alreadySorted: 0,
        quoted: 0,
        notReady: 0,
        sold: 0,
        total: 0
      }));
    }
  }, [leads]); // Only recalculate when leads change

  // Calculate KPI data from leads
  const calculateKPIData = (): KPIData => {
    // Safety check for leads array
    if (!Array.isArray(leads)) {
      return {
        totalCustomers: 0,
        totalLeads: 0,
        activeProjects: 0,
        completedProjects: 0,
        totalRevenue: 0,
        pendingInvoices: 0,
        newLeadsThisMonth: 0,
        projectsCompletedThisMonth: 0,
      };
    }

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const leadsThisMonth = leads.filter(lead => {
      const leadDate = new Date(lead.created_at);
      return leadDate.getMonth() === currentMonth && leadDate.getFullYear() === currentYear;
    });
    
    const wonLeads = leads.filter(lead => lead.status === 'won');
    const totalRevenue = wonLeads.reduce((sum, lead) => sum + (lead.estimated_value || 0), 0);
    // Count active projects from actual projects data
    const activeProjectsCount = Array.isArray(projects) ? projects.filter(project => 
      project.status && project.status.toLowerCase() !== 'completed'
    ).length : 0;
    const completedProjectsCount = Array.isArray(projects) ? projects.filter(project => 
      project.status && project.status.toLowerCase() === 'completed'
    ).length : 0;
    
    return {
      totalCustomers: leads.length,
      totalLeads: leads.length,
      activeProjects: activeProjectsCount,
      completedProjects: completedProjectsCount,
      totalRevenue: totalRevenue,
      pendingInvoices: leads.filter(lead => lead.status === 'quoted').length,
      newLeadsThisMonth: leadsThisMonth.length,
      projectsCompletedThisMonth: leadsThisMonth.filter(lead => lead.status === 'won').length,
    };
  };

  // Calculate pipeline data from leads
  const calculatePipelineData = (): PipelineData[] => {
    // Safety check for leads array
    if (!Array.isArray(leads)) {
      return [];
    }

    const statusCounts = leads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const statusValues = leads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + (lead.estimated_value || 0);
      return acc;
    }, {} as Record<string, number>);
    
    const pipelineData = Object.entries(statusCounts).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
      value: count,
      total_value: statusValues[status] || 0,
    }));
    
    return pipelineData;
  };

  useEffect(() => {
    // Log dashboard mount
    sendDebugLog('Dashboard component mounted', {
      isMobile,
      hasChartsRendered: false
    });

    // Refresh leads data to ensure we have the latest counts
    refreshLeads();

    // Fetch dashboard data only on mount
    fetchDashboardData();

    // Log when component unmounts
    return () => {
      sendDebugLog('Dashboard component unmounting', {});
    };
  }, []); // Empty dependency array - only run once on mount

  // Fetch lead sources when period changes
  useEffect(() => {
    const fetchLeadSources = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/dashboard/lead-sources?months=${leadSourcesPeriod}`);
        setLeadSources(response.data);
      } catch (error) {
        console.error('Error fetching lead sources:', error);
      }
    };

    fetchLeadSources();
  }, [leadSourcesPeriod]);

  // Helper function to transform API lead status data to table format
  const calculateStatusStatsFromAPIData = (statusData: any[]) => {
    const months = getLast6Months();
    const statsArray: any[] = [];
    
    // Define all categories we want to show in the table
    const categories = [
      { category: 'NEW', status: 'new' },
      { category: 'CONTACTED', status: 'contacted' },
      { category: 'JUNK', status: 'lost', lost_status: 'junk' },
      { category: 'OUT OF AREA', status: 'lost', lost_status: 'out_of_area' },
      { category: 'NEW/SPRAY', status: 'lost', lost_status: 'new_spray' },
      { category: 'NOT INTERESTED', status: 'lost', lost_status: 'not_interested' },
      { category: 'WRONG NUMBER', status: 'lost', lost_status: 'wrong_number' },
      { category: 'ALREADY SORTED', status: 'lost', lost_status: 'already_sorted' },
      { category: 'NO REPLY', status: 'no_reply' },
      { category: 'PRICE', status: 'lost', lost_status: 'price' },
      { category: 'QUOTED', status: 'quoted' },
      { category: 'NOT READY', status: 'not_ready' },
      { category: 'WON', status: 'won' }
    ];
    
    // Create a row for each category
    categories.forEach(cat => {
      const obj: any = { category: cat.category, total: 0 };
      
      // Initialize all month columns
      months.forEach(month => {
        obj[month.toLowerCase()] = 0;
      });
      
      // Find matching data from API
      if (cat.lost_status) {
        // For lost sub-statuses, find the specific lost_status match
        const matchingData = statusData.find(item => 
          item.status === 'lost' && item.lost_status === cat.lost_status
        );
        if (matchingData) {
          obj.total = parseInt(matchingData.total) || 0;
          // Use actual monthly data if available
          if (matchingData.monthlyData) {
            months.forEach(month => {
              const monthValue = matchingData.monthlyData[month] || 0;
              obj[month.toLowerCase()] = parseInt(monthValue) || 0;
            });
          }
        }
      } else {
        // For regular statuses, find by status only
        const matchingData = statusData.find(item => 
          item.status === cat.status && !item.lost_status
        );
        if (matchingData) {
          obj.total = parseInt(matchingData.total) || 0;
          // Use actual monthly data if available
          if (matchingData.monthlyData) {
            months.forEach(month => {
              const monthValue = matchingData.monthlyData[month] || 0;
              obj[month.toLowerCase()] = parseInt(monthValue) || 0;
            });
          }
        }
      }
      
      statsArray.push(obj);
    });
    
    return statsArray;
  };

  const fetchDashboardData = async () => {
    try {
      const startTime = Date.now();
      
      // Try the optimized endpoint first
      try {
        const response = await axios.get(`/api/dashboard-fast/all`);
        const data = response.data;
        
        // Set all data from the single response
        if (data.kpis) {
          setBackendKPIData(data.kpis);
        }
        
        if (data.leadStatus) {
          // Transform lead status data for the status table
          const statusStats = calculateStatusStatsFromAPIData(data.leadStatus);
          setLeadStatusStats(statusStats);
        }
        
        if (data.leadSources) {
          setLeadSources(data.leadSources);
        }
        
        // Set pipeline data if we have it
        if (data.pipelineData) {
          const pipelineArray = [
            { name: 'New', value: data.pipelineData.new || 0 },
            { name: 'Contacted', value: data.pipelineData.contacted || 0 },
            { name: 'Not Ready', value: data.pipelineData.not_ready || 0 },
            { name: 'Quoted', value: data.pipelineData.quoted || 0 },
            { name: 'Won', value: data.pipelineData.won || 0 },
            { name: 'Lost', value: data.pipelineData.lost || 0 }
          ].filter(item => item.value > 0); // Only show categories with data
          setPipelineData(pipelineArray);
        }
        
        const loadTime = Date.now() - startTime;
        console.log(`Dashboard loaded in ${loadTime}ms (optimized)`);
        
        setLoading(false);
        return;
      } catch (optimizedError) {
        console.log('Optimized endpoint failed, falling back to original endpoints');
      }
      
      // Fallback to original implementation
      const safeLeads = Array.isArray(leads) ? leads : [];
      
      // Fetch KPI data from backend
      try {
        const kpiResponse = await axios.get(`/api/dashboard/kpis`);
        
        setBackendKPIData(kpiResponse.data);
      } catch (error) {
        console.error('Error fetching KPI data:', error);
      }

      // Debug: Log leads to see what we're working with
      
      // Use calculated data from leads instead of API calls
      const calculatedPipelineData = calculatePipelineData();
      setPipelineData(calculatedPipelineData);
      
      // Fetch projects data from API
      try {
        const projectsResponse = await axios.get(`${API_BASE_URL}/projects`);
        
        // Ensure we set an array - handle both array response and object with data property
        if (Array.isArray(projectsResponse.data)) {
          setProjects(projectsResponse.data);
        } else if (projectsResponse.data && Array.isArray(projectsResponse.data.data)) {
          setProjects(projectsResponse.data.data);
        } else if (projectsResponse.data && Array.isArray(projectsResponse.data.projects)) {
          setProjects(projectsResponse.data.projects);
        } else {
          setProjects([]);
          console.warn('Projects API response is not in expected format:', projectsResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        setProjects([]);
      }
      
      // Fetch lead sources data from API
      try {
        const leadSourcesResponse = await axios.get(`${API_BASE_URL}/dashboard/lead-sources`);
        
        setLeadSources(leadSourcesResponse.data);
      } catch (error) {
        console.error('Error fetching lead sources:', error);
      }
      
      // Generate recent activities from lead actions
      const recentActivities = leads
        .flatMap(lead => (lead.actions || []).map(action => ({
          type: action.action_type.toLowerCase().replace(' ', '_'),
          title: `${action.action_type}: ${lead.customer_name}`,
          description: action.description,
          created_at: action.created_at,
        })))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);
      
      setActivities(recentActivities);
      
      // Set display months for the table (this is still needed for the status table)
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const months: string[] = [];
      const monthYears: number[] = []; // Need this for status table calculations
      
      for (let i = 5; i >= 0; i--) {
        let monthIndex = currentMonth - i;
        let year = currentYear;
        if (monthIndex < 0) {
          monthIndex += 12;
          year = currentYear - 1;
        }
        months.push(monthNames[monthIndex]);
        monthYears.push(year);
      }
      
      setDisplayMonths(months); // Store the months for table display
      
      // Calculate real status stats table data with dynamic month columns
      const statusData: LeadStatusStats[] = [];
      
      // Helper function to count by month
      const countByMonth = (filterFn: (lead: any) => boolean) => {
        const counts: any = { total: 0 };
        // Initialize all month columns
        months.forEach(month => {
          counts[month.toLowerCase()] = 0;
        });
        
        leads.forEach(lead => {
          if (filterFn(lead)) {
            const leadDate = new Date(lead.created_at);
            const leadMonth = leadDate.getMonth();
            const leadYear = leadDate.getFullYear();
            const monthName = monthNames[leadMonth];
            
            // Check if this month is in our display range
            const monthIndex = months.indexOf(monthName);
            if (monthIndex !== -1) {
              const expectedYear = monthYears[monthIndex]; // Use the stored year for this month
              if (leadYear === expectedYear) {
                counts[monthName.toLowerCase()]++;
                counts.total++;
              }
            }
          }
        });
        return counts;
      };
      
      statusData.push({
        category: 'NEW',
        ...countByMonth(lead => lead.status === 'new')
      });
      
      statusData.push({
        category: 'CONTACTED',
        ...countByMonth(lead => lead.status === 'contacted')
      });
      
      statusData.push({
        category: 'JUNK',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'junk')
      });
      
      statusData.push({
        category: 'OUT OF AREA',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'out_of_area')
      });
      
      statusData.push({
        category: 'NEW/SPRAY',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'new_spray')
      });
      
      statusData.push({
        category: 'NO REPLY',
        ...countByMonth(lead => lead.status === 'no_reply')
      });
      
      statusData.push({
        category: 'NOT INTERESTED',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'not_interested')
      });
      
      statusData.push({
        category: 'WRONG NUMBER',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'wrong_number')
      });
      
      statusData.push({
        category: 'ALREADY SORTED',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'already_sorted')
      });

      statusData.push({
        category: 'PRICE',
        ...countByMonth(lead => lead.status === 'lost' && lead.lost_status === 'price')
      });

      statusData.push({
        category: 'QUOTED',
        ...countByMonth(lead => lead.status === 'quoted')
      });
      
      statusData.push({
        category: 'NOT READY',
        ...countByMonth(lead => lead.status === 'not_ready')
      });
      
      statusData.push({
        category: 'WON', // Changed from SOLD to WON
        ...countByMonth(lead => lead.status === 'won')
      });

      setLeadStatusStats(statusData);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Keep structure but with empty data
      setActivities([]);
      setPipelineData([]);
      // Don't clear displayMonths - keep the months displayed
      // setDisplayMonths([]);
      
      // Keep the structure of leadStatusStats but with zero values
      const months = getLast6Months();
      const emptyMonthData = Object.fromEntries(months.map(m => [m, 0]));
      setLeadStatusStats([
        { category: 'NEW', ...emptyMonthData, total: 0 },
        { category: 'CONTACTED', ...emptyMonthData, total: 0 },
        { category: 'QUOTED', ...emptyMonthData, total: 0 },
        { category: 'WON', ...emptyMonthData, total: 0 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      new: '#3498db',
      contacted: '#f39c12',
      quoted: '#9b59b6',
      won: '#27ae60',
      lost: '#e74c3c',
    };
    return colors[status] || '#95a5a6';
  };

  // Don't replace entire component during loading - keep structure intact for mobile menu
  const isLoading = loading || leadsLoading;

  return (
    <Box>

      {/* Always render the page structure, use skeletons for loading data */}
      {/* Mobile Quick Navigation Buttons */}
      {isMobile && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            px: 1
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/leads')}
              onTouchEnd={(e) => {
                e.preventDefault();
                navigate('/leads');
              }}
              sx={{
                flex: 1,
                maxWidth: '180px',
                height: '56px',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                },
              }}
              startIcon={<TrendingUp />}
            >
              Leads
            </Button>
            
            <Button
              variant="contained"
              color="success"
              onClick={() => navigate('/projects')}
              onTouchEnd={(e) => {
                e.preventDefault();
                navigate('/projects');
              }}
              sx={{
                flex: 1,
                maxWidth: '180px',
                height: '56px',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                },
              }}
              startIcon={<Work />}
            >
              Projects
            </Button>
          </Box>
        </Box>
      )}

      {/* KPI Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
        gridAutoRows: '1fr',
        gap: 3,
        mb: 4
      }}>
        <Box>
          <Card sx={{ height: '100%', minHeight: '140px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Total Contacts
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}>
                    {backendKPIData?.totalCustomers ?? ''}
                  </Typography>
                  <Typography variant="body2" color="success.main" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                    {backendKPIData?.newContactsThisMonth !== undefined && `${backendKPIData.newContactsThisMonth} new this month`}
                  </Typography>
                </Box>
                <People sx={{ fontSize: { xs: 30, sm: 35, md: 40 }, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ height: '100%', minHeight: '140px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Active Leads
                    </Typography>
                    <MuiTooltip title="Quoted / Not Ready" arrow>
                      <InfoOutlined sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.secondary', cursor: 'help' }} />
                    </MuiTooltip>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}>
                    {calculateKPIData().totalLeads || ''}
                  </Typography>
                  <Typography variant="body2" color="warning.main" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                    {calculateKPIData().newLeadsThisMonth || ''} {calculateKPIData().newLeadsThisMonth > 0 && 'total this month'}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: { xs: 30, sm: 35, md: 40 }, color: 'secondary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ height: '100%', minHeight: '140px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Active Projects
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}>
                    {backendKPIData?.activeProjects ?? ''}
                  </Typography>
                  <Typography variant="body2" color="info.main" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                    {backendKPIData?.newProjectsThisMonth !== undefined && `${backendKPIData.newProjectsThisMonth} new this month`}
                  </Typography>
                </Box>
                <Work sx={{ fontSize: { xs: 30, sm: 35, md: 40 }, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ height: '100%', minHeight: '140px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Project Costs This Month
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}>
                    {backendKPIData?.revenueThisMonth !== undefined ? formatCurrency(backendKPIData.revenueThisMonth) : ''}
                  </Typography>
                  <Typography variant="body2" color="success.main" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                    {backendKPIData?.totalProjects !== undefined && `${backendKPIData.totalProjects} projects started this month`}
                  </Typography>
                </Box>
                <Receipt sx={{ fontSize: { xs: 30, sm: 35, md: 40 }, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ height: '100%', minHeight: '140px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Remedials
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}>
                    {backendKPIData?.remedialProjects ?? ''}
                  </Typography>
                  <Typography variant="body2" color="error.main" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                    {backendKPIData?.remedialProjects !== undefined && 'projects requiring fixes'}
                  </Typography>
                </Box>
                <Build sx={{ fontSize: { xs: 30, sm: 35, md: 40 }, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts and Tables Row */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 3, 
        mb: 4 
      }}>
        {/* Monthly Lead Trends Chart */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card sx={{ height: '620px', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Monthly Lead Trends
              </Typography>
              <Box sx={{
                flex: 1,
                width: '100%',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {monthlyStats && monthlyStats.length > 0 ? (
                  <MemoizedLineChart data={monthlyStats} />
                ) : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                  }}>
                    <Typography color="textSecondary">
                      {monthlyStats ? 'No chart data available' : ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Lead Status Statistics */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card sx={{ height: { xs: 'auto', md: '620px' }, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Lead Status - Last 6 Months
              </Typography>
              <TableContainer sx={{ overflowX: 'auto', overflowY: 'auto', flex: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: '8px' }}>LEAD STATUS</TableCell>
                      {displayMonths.map(month => (
                        <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: '8px' }}>
                          {month}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: '8px', borderLeft: '2px solid #bdbdbd' }}>TOTAL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadStatusStats.map((row, index) => {
                      const isNew = row.category === 'NEW';
                      const isContacted = row.category === 'CONTACTED';
                      const isWon = row.category === 'WON';
                      const isQuoted = row.category === 'QUOTED';
                      const rowColor = isNew ? '#ffebee' : isContacted ? '#fff3e0' : isWon ? '#e8f5e9' : isQuoted ? '#e3f2fd' : 'inherit';
                      
                      return (
                        <TableRow key={index} sx={{ backgroundColor: rowColor }}>
                          <TableCell sx={{
                            fontWeight: isNew || isContacted || isWon ? 'bold' : 'normal',
                            fontSize: isNew || isContacted ? '0.85rem' : '0.875rem'
                          }}>
                            {row.category}
                          </TableCell>
                          {displayMonths.map(month => (
                            <TableCell key={month} align="center">
                              {row[month.toLowerCase()] || '-'}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {row.total}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Total Row */}
                    <TableRow sx={{ backgroundColor: '#1976d2' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'white', fontSize: '0.875rem' }}>
                        TOTAL
                      </TableCell>
                      {displayMonths.map(month => {
                        const monthTotal = leadStatusStats.reduce((sum, row) => {
                          const val = row[month.toLowerCase()];
                          return sum + (typeof val === 'number' ? val : 0);
                        }, 0);
                        return (
                          <TableCell key={month} align="center" sx={{ fontWeight: 'bold', color: 'white' }}>
                            {monthTotal}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white', borderLeft: '2px solid #1565c0' }}>
                        {leadStatusStats.reduce((sum, row) => sum + (row.total || 0), 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Follow Up Tracker */}
      <Box sx={{ mb: 4 }}>
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Follow Up Tracker
              </Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={followUpYear}
                  onChange={(e) => setFollowUpYear(Number(e.target.value))}
                >
                  {[2025, 2026, 2027].map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f44336', color: 'white', padding: '12px 8px' }}>
                      OVERDUE
                    </TableCell>
                    {['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'].map(month => (
                      <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd', padding: '12px 8px' }}>
                        {month}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', padding: '12px 8px' }}>
                      TOTAL
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {(() => {
                      const monthCounts: number[] = [];
                      let total = 0;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      // Count overdue follow-ups (any follow-up date before today, excluding won/lost)
                      const overdueCount = leads.filter(lead => {
                        if (!lead.next_follow_up) return false;
                        if (lead.status === 'won' || lead.status === 'lost') return false;
                        const followUpDate = new Date(lead.next_follow_up);
                        followUpDate.setHours(0, 0, 0, 0);
                        return followUpDate < today;
                      }).length;

                      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
                        const count = leads.filter(lead => {
                          if (!lead.next_follow_up) return false;
                          if (lead.status === 'won' || lead.status === 'lost') return false;
                          const followUpDate = new Date(lead.next_follow_up);
                          return followUpDate.getMonth() === monthIndex && followUpDate.getFullYear() === followUpYear;
                        }).length;
                        monthCounts.push(count);
                        total += count;
                      }

                      return (
                        <>
                          {/* Overdue cell */}
                          <TableCell
                            align="center"
                            onClick={() => {
                              if (overdueCount > 0) {
                                navigate(`/follow-up?month=0&year=0&status=overdue`);
                              }
                            }}
                            sx={{
                              fontSize: '1.1rem',
                              fontWeight: overdueCount > 0 ? 'bold' : 'normal',
                              color: overdueCount > 0 ? 'white' : 'text.secondary',
                              backgroundColor: overdueCount > 0 ? '#f44336' : '#ffcdd2',
                              cursor: overdueCount > 0 ? 'pointer' : 'default',
                              '&:hover': overdueCount > 0 ? {
                                backgroundColor: '#d32f2f',
                                textDecoration: 'underline'
                              } : {}
                            }}
                          >
                            {overdueCount || '-'}
                          </TableCell>
                          {monthCounts.map((count, index) => {
                            // Check if this month has overdue items (past months in current year or earlier years)
                            const currentMonth = today.getMonth();
                            const currentYear = today.getFullYear();
                            const isPastMonth = (followUpYear < currentYear) || (followUpYear === currentYear && index < currentMonth);
                            const hasOverdue = isPastMonth && count > 0;

                            return (
                              <TableCell
                                key={index}
                                align="center"
                                onClick={() => {
                                  if (count > 0) {
                                    navigate(`/follow-up?month=${index + 1}&year=${followUpYear}`);
                                  }
                                }}
                                sx={{
                                  fontSize: '1.1rem',
                                  fontWeight: count > 0 ? 'bold' : 'normal',
                                  color: hasOverdue ? '#f44336' : count > 0 ? '#1976d2' : 'text.secondary',
                                  backgroundColor: hasOverdue ? '#ffebee' : count > 0 ? '#e3f2fd' : 'inherit',
                                  cursor: count > 0 ? 'pointer' : 'default',
                                  '&:hover': count > 0 ? {
                                    backgroundColor: hasOverdue ? '#ffcdd2' : '#bbdefb',
                                    textDecoration: 'underline'
                                  } : {}
                                }}
                              >
                                {count || '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: '1.1rem',
                              fontWeight: 'bold',
                              backgroundColor: '#1976d2',
                              color: 'white'
                            }}
                          >
                            {total}
                          </TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* NEW Lead Status - Last 6 Months Widget (Fresh Implementation) - HIDDEN */}
      {/* <Box sx={{ mb: 4 }}>
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Lead Status - Last 6 Months (NEW)
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: '8px' }}>
                      LEAD STATUS
                    </TableCell>
                    {(() => {
                      // Generate last 6 months
                      const currentDate = new Date();
                      const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                      const last6Months = [];
                      
                      for (let i = 5; i >= 0; i--) {
                        const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                        last6Months.push(months[d.getMonth()]);
                      }
                      
                      return last6Months.map(month => (
                        <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: '8px' }}>
                          {month}
                        </TableCell>
                      ));
                    })()}
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: '8px', borderLeft: '2px solid #bdbdbd' }}>
                      TOTAL
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Calculate the date range for last 6 months
                    const currentDate = new Date();
                    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                    const monthsToShow: string[] = [];
                    const monthIndices: number[] = [];
                    const yearMonthPairs: { month: number; year: number }[] = [];
                    
                    for (let i = 5; i >= 0; i--) {
                      const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                      monthsToShow.push(months[d.getMonth()]);
                      monthIndices.push(d.getMonth());
                      yearMonthPairs.push({ month: d.getMonth(), year: d.getFullYear() });
                    }
                    
                    // Status categories to display
                    const statusCategories = [
                      { name: 'NEW', filter: (lead: any) => lead.status === 'new' },
                      { name: 'CONTACTED', filter: (lead: any) => lead.status === 'contacted' },
                      { name: 'JUNK', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'junk' },
                      { name: 'OUT OF AREA', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'out_of_area' },
                      { name: 'NEW/SPRAY', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'new_spray' },
                      { name: 'NO REPLY', filter: (lead: any) => lead.status === 'no_reply' },
                      { name: 'NOT INTERESTED', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'not_interested' },
                      { name: 'WRONG NUMBER', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'wrong_number' },
                      { name: 'ALREADY SORTED', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'already_sorted' },
                      { name: 'PRICE', filter: (lead: any) => lead.status === 'lost' && lead.lost_status === 'price' },
                      { name: 'QUOTED', filter: (lead: any) => lead.status === 'quoted' },
                      { name: 'NOT READY', filter: (lead: any) => lead.status === 'not_ready' },
                      { name: 'WON', filter: (lead: any) => lead.status === 'won' },
                    ];
                    
                    return statusCategories.map((category, index) => {
                      // Count leads for each month
                      const monthCounts: { [key: string]: number } = {};
                      let total = 0;
                      
                      yearMonthPairs.forEach((pair, idx) => {
                        const monthName = monthsToShow[idx];
                        const count = leads.filter(lead => {
                          if (!lead.created_at) return false;
                          const leadDate = new Date(lead.created_at);
                          return leadDate.getMonth() === pair.month && 
                                 leadDate.getFullYear() === pair.year && 
                                 category.filter(lead);
                        }).length;
                        monthCounts[monthName] = count;
                        total += count;
                      });
                      
                      const isHighlight = category.name === 'NEW' || category.name === 'CONTACTED' || 
                                        category.name === 'WON' || category.name === 'QUOTED';
                      const rowColor = category.name === 'NEW' ? '#ffebee' : 
                                     category.name === 'CONTACTED' ? '#fff3e0' : 
                                     category.name === 'WON' ? '#e8f5e9' : 
                                     category.name === 'QUOTED' ? '#e3f2fd' : 'inherit';
                      
                      return (
                        <TableRow key={category.name} sx={{ backgroundColor: rowColor }}>
                          <TableCell sx={{ 
                            fontWeight: isHighlight ? 'bold' : 'normal',
                            fontSize: '0.875rem'
                          }}>
                            {category.name}
                          </TableCell>
                          {monthsToShow.map(month => (
                            <TableCell key={month} align="center">
                              {monthCounts[month] || '-'}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderLeft: '1px solid #e0e0e0' }}>
                            {total || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box> */}

      {/* Sales Pipeline and Lead Sources Row */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 3, 
        mb: 4 
      }}>
        {/* Sales Pipeline */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sales Pipeline (6 Months)
              </Typography>
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      isAnimationActive={false}
                    >
                      {pipelineData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={KPI_COLORS[index % KPI_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} leads`, name]}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                      labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Lead Sources Analysis */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Lead Sources
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={leadSourcesPeriod}
                    onChange={(e) => setLeadSourcesPeriod(Number(e.target.value))}
                  >
                    <MenuItem value={1}>Last Month</MenuItem>
                    <MenuItem value={3}>Last 3 Months</MenuItem>
                    <MenuItem value={6}>Last 6 Months</MenuItem>
                    <MenuItem value={12}>Last 12 Months</MenuItem>
                    <MenuItem value={0}>Year to Date</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ height: 250, overflow: 'auto' }}>
                {leadSources.length > 0 ? (
                  <Box>
                    {leadSources.map((source, index) => (
                      <Box
                        key={source.source}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: index % 2 === 0 ? 'grey.50' : 'transparent',
                          mb: 1
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {source.source}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {source.converted}/{source.count} converted
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {source.count}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'success.main' }}
                          >
                            {source.conversionRate}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="textSecondary">
                      No lead source data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}